import {
  BellOutlined,
  GlobalOutlined,
  KeyOutlined,
  LogoutOutlined,
  MoonOutlined,
  QrcodeOutlined,
  RightOutlined,
  SunOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../store/hooks";
import { themeColorLabelKey, themePaletteOf } from "../../lib/themePalettes";
import ProfileMenuRow from "./ProfileMenuRow";
import { LANGUAGES } from "./languages";

/** Редът „Смяна на код или парола": етикетът според това какво има човекът,
 * `offlineHint` — изключен в локален режим (заявката е само към облака). */
export interface CredentialsMenuEntry {
  label: string;
  offlineHint?: string;
}

interface ProfileMenuProps {
  displayName: string;
  username: string;
  access: boolean;
  onLogout: () => void;
  onOpenTheme: () => void;
  onOpenLanguage: () => void;
  onOpenQr: () => void;
  showPush: boolean;
  onOpenPush: () => void;
  /** null — човекът няма нито код, нито парола за смяна (редът не се показва). */
  credentials: CredentialsMenuEntry | null;
  onOpenCredentials: () => void;
  logoutLabelKey: string;
  revokeAccessLabelKey: string;
}

// Съдържанието на падащото меню под аватара (ProfileDropdown).
export default function ProfileMenu({
  displayName,
  username,
  access,
  onLogout,
  onOpenTheme,
  onOpenLanguage,
  onOpenQr,
  showPush,
  onOpenPush,
  credentials,
  onOpenCredentials,
  logoutLabelKey,
  revokeAccessLabelKey,
}: ProfileMenuProps) {
  const { t, i18n } = useTranslation();
  const initial =
    (displayName?.trim()?.[0] || username?.trim()?.[0] || "?").toUpperCase();

  const currentLang = i18n.language?.startsWith("bg") ? "bg" : "en";
  const currentLangLabel =
    LANGUAGES.find((language) => language.key === currentLang)?.label ??
    currentLang;

  const currentTheme = themePaletteOf(
    useAppSelector((state) => state.theme.mode),
  );
  // Само цветът — светла/тъмна казва иконата вляво (слънце/луна); иначе
  // „Небесно синьо · Светла“ не се побира в 260px и се залепя за „Тема“.
  const currentThemeLabel = t(themeColorLabelKey(currentTheme.color));

  // Стойността вдясно се реже с „…“, а не избутва етикета.
  const currentValue = (text: string) => (
    <span className="flex min-w-0 max-w-[55%] items-center gap-1.5 text-[12px] font-semibold text-muted">
      <span className="truncate">{text}</span>
      <RightOutlined className="shrink-0 text-[10px]" />
    </span>
  );

  return (
    <div className="w-[260px] overflow-hidden rounded-[14px] border border-shade-2 bg-surface shadow-[0_12px_32px_rgba(17,17,17,0.10),0_2px_8px_rgba(17,17,17,0.05)]">
      {/* Identity — в тестов режим с кехлибарено сияние в ъгъла. */}
      <div
        className={`flex items-center gap-3 bg-surface px-4 pt-4 pb-3.5 ${
          access
            ? "bg-[radial-gradient(ellipse_90%_120%_at_100%_0%,var(--color-brand-tint)_0%,transparent_70%)]"
            : ""
        }`}
      >
        <div className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-brand text-[16px] font-bold tracking-[-0.3px] text-white shadow-[0_2px_8px_color-mix(in_srgb,var(--color-brand)_25%,transparent)]">
          {initial}
          {access && (
            <span
              title={t("modules.accessOn")}
              className="absolute -right-0.5 -bottom-0.5 h-3 w-3 rounded-full border-2 border-surface bg-brand shadow-[0_0_0_2px_color-mix(in_srgb,var(--color-brand)_25%,transparent)]"
            />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[14px] font-bold tracking-[-0.2px] text-ink">
            {displayName || username || "—"}
          </div>
          {username && displayName && username !== displayName && (
            <div className="mt-0.5 truncate text-[11.5px] text-muted">
              @{username}
            </div>
          )}
        </div>
      </div>

      <div className="h-px bg-shade-2" />

      {/* Тема + Език + QR код + Известия + Смяна на код или парола */}
      <div className="p-1.5">
        <ProfileMenuRow
          icon={
            currentTheme.isDark ? (
              <MoonOutlined className="text-[14px]" />
            ) : (
              <SunOutlined className="text-[14px]" />
            )
          }
          label={t("common.theme")}
          trailing={currentValue(currentThemeLabel)}
          onClick={onOpenTheme}
        />
        <ProfileMenuRow
          icon={<GlobalOutlined className="text-[14px]" />}
          label={t("common.language")}
          trailing={currentValue(currentLangLabel)}
          onClick={onOpenLanguage}
        />
        <ProfileMenuRow
          icon={<QrcodeOutlined className="text-[14px]" />}
          label={t("common.qrTitle")}
          onClick={onOpenQr}
        />
        {showPush && (
          <ProfileMenuRow
            icon={<BellOutlined className="text-[14px]" />}
            label={t("push.menuLabel")}
            onClick={onOpenPush}
          />
        )}
        {credentials && (
          <ProfileMenuRow
            icon={<KeyOutlined className="text-[14px]" />}
            label={credentials.label}
            disabled={Boolean(credentials.offlineHint)}
            description={credentials.offlineHint}
            onClick={onOpenCredentials}
          />
        )}
      </div>

      <div className="h-px bg-shade-2" />

      {/* Изход */}
      <div className="p-1.5">
        <ProfileMenuRow
          icon={<LogoutOutlined className="text-[14px]" />}
          label={access ? t(revokeAccessLabelKey) : t(logoutLabelKey)}
          danger
          onClick={onLogout}
        />
      </div>
    </div>
  );
}
